import React, { useMemo, useState } from 'react';
import MaterialTable from 'material-table';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@mui/material/styles';
import { Autocomplete, Box, Grid, LinearProgress, TextField, Typography } from '@mui/material';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { api } from 'common';
import { withFallbackTranslations } from 'common/src/other/fallbackTranslations';
import langlocales from '../lists/langlocales';
import datelocales from '../lists/datelocales';
import CircularLoading from '../components/CircularLoading';
import localization from '../components/Table/Localization';
import C2Dialog, { C2DangerButton, C2PrimaryButton, C2SecondaryButton } from '../components/C2Dialog';
import {
  C2,
  C2_FONT,
  C2AddButton,
  C2DocsChip,
  C2EmptyState,
  C2SectionHeading,
  C2StatusChip,
  C2TableCard,
  c2Localization,
  c2TableOptions,
  c2TableStyle,
  c2TableTheme,
  c2Toolbar,
} from '../components/Table/C2UserTable';
import { DemoModeAlert, SettingsField, SettingsNotice, settingsFieldSx } from '../components/settings/C2SettingsKit';

const MAX_VISIBLE_KEYS = 150;
const TRANSLATE_CONCURRENCY = 6;

const languageName = (code) => {
  try {
    const name = new Intl.DisplayNames(['es'], { type: 'language' }).of(code);
    return name && name !== code ? name.charAt(0).toUpperCase() + name.slice(1) : code;
  } catch (e) {
    return code;
  }
};

const LANG_OPTIONS = Object.keys(langlocales).map((code) => ({ code, label: `${languageName(code)} (${code})` }));
const DATE_OPTIONS = Object.keys(datelocales).map((code) => ({ code, label: code }));

export default function LanguageSetting() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { editLanguage, translateText } = api;
  const dispatch = useDispatch();
  const languagedata = useSelector((state) => state.languagedata);
  const settings = useSelector((state) => state.settingsdata.settings);
  const canEdit = !!settings?.AllowCriticalEditsAdmin;
  const languages = useMemo(
    () => (languagedata.langlist || []).map(({ tableData, ...l }) => ({ ...l, keysCount: Object.keys(withFallbackTranslations(l.langLocale, l.keyValuePairs)).length })),
    [languagedata.langlist]
  );
  const defaultLang = languages.find((l) => l.default) || null;

  const [notice, setNotice] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [meta, setMeta] = useState(null);
  const [texts, setTexts] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  const guardEdit = () => {
    if (!canEdit) setNotice({ severity: 'info', message: 'Modo demostración: los cambios no se guardan.' });
    return canEdit;
  };

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      setNotice({ severity: 'success', message: successMsg });
      return true;
    } catch (e) {
      setNotice({ severity: 'error', message: `No se pudo guardar: ${e?.message || e}` });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const makeDefault = (row) => {
    if (!guardEdit() || row.default) return;
    const writes = languages
      .filter((l) => !!l.default !== (l.id === row.id))
      .map(({ keysCount, ...l }) => dispatch(editLanguage({ ...l, default: l.id === row.id }, 'Update')));
    run(() => Promise.all(writes), `${row.langName} es ahora el idioma predeterminado.`);
  };

  const translateAll = async (source, from, to) => {
    const keys = Object.keys(source);
    const out = {};
    let done = 0;
    let cursor = 0;
    setProgress({ done: 0, total: keys.length });
    const worker = async () => {
      while (cursor < keys.length) {
        const key = keys[cursor++];
        const text = await translateText(source[key], from, to);
        out[key] = text || source[key];
        done += 1;
        setProgress({ done, total: keys.length });
      }
    };
    await Promise.all(Array.from({ length: Math.min(TRANSLATE_CONCURRENCY, keys.length) }, worker));
    return out;
  };

  const saveMeta = async () => {
    const langName = String(meta.langName || '').trim();
    if (!langName || !meta.langLocale || !meta.dateLocale) {
      setMeta({ ...meta, error: 'Completa nombre, idioma y formato de fecha.' });
      return;
    }
    if (languages.some((l) => l.id !== meta.id && l.langLocale === meta.langLocale)) {
      setMeta({ ...meta, error: 'Ya existe un idioma con ese código.' });
      return;
    }
    if (meta.id) {
      const current = languages.find((l) => l.id === meta.id);
      const { keysCount, ...base } = current;
      const ok = await run(() => dispatch(editLanguage({ ...base, langName, langLocale: meta.langLocale, dateLocale: meta.dateLocale }, 'Update')), 'Idioma actualizado.');
      if (ok) setMeta(null);
      return;
    }
    const from = defaultLang?.langLocale || 'es';
    const source = withFallbackTranslations(from, defaultLang?.keyValuePairs);
    const ok = await run(async () => {
      const keyValuePairs = await translateAll(source, from, meta.langLocale);
      await dispatch(editLanguage({
        langName,
        langLocale: meta.langLocale,
        dateLocale: meta.dateLocale,
        createdAt: Date.now(),
        default: false,
        keyValuePairs,
      }, 'Add'));
    }, `${langName} agregado y traducido desde ${defaultLang?.langName || 'el idioma predeterminado'}.`);
    setProgress(null);
    if (ok) setMeta(null);
  };

  const openTexts = (row) => {
    setTexts({ id: row.id, langName: row.langName, search: '', edits: {} });
  };

  const saveTexts = async () => {
    if (!guardEdit()) return;
    const current = languages.find((l) => l.id === texts.id);
    const changed = Object.keys(texts.edits).length;
    if (!current || !changed) {
      setTexts(null);
      return;
    }
    const { keysCount, ...base } = current;
    const ok = await run(
      () => dispatch(editLanguage({ ...base, keyValuePairs: { ...(base.keyValuePairs || {}), ...texts.edits } }, 'Update')),
      `${changed} texto${changed === 1 ? '' : 's'} actualizado${changed === 1 ? '' : 's'} en ${current.langName}.`
    );
    if (ok) setTexts(null);
  };

  const confirmDelete = async () => {
    const { keysCount, ...lang } = toDelete;
    const ok = await run(() => dispatch(editLanguage(lang, 'Delete')), `${toDelete.langName} eliminado.`);
    if (ok) setToDelete(null);
  };

  const textRows = useMemo(() => {
    if (!texts) return { rows: [], total: 0 };
    const lang = languages.find((l) => l.id === texts.id);
    const pairs = Object.entries(withFallbackTranslations(lang?.langLocale, lang?.keyValuePairs));
    const q = texts.search.trim().toLowerCase();
    const filtered = q ? pairs.filter(([k, v]) => k.toLowerCase().includes(q) || String(v).toLowerCase().includes(q)) : pairs;
    return { rows: filtered.slice(0, MAX_VISIBLE_KEYS), total: filtered.length };
  }, [texts, languages]);

  const columns = [
    {
      title: 'Idioma',
      field: 'langName',
      render: (row) => (
        <Box>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.9rem', color: C2.dark }}>{row.langName}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.76rem', color: C2.muted }}>{languageName(row.langLocale)}</Typography>
        </Box>
      ),
    },
    { title: 'Código', field: 'langLocale' },
    { title: 'Formato de fecha', field: 'dateLocale' },
    { title: 'Textos', field: 'keysCount', type: 'numeric' },
    {
      title: 'Estado',
      field: 'default',
      render: (row) => (row.default ? <C2StatusChip tone="success" label="Predeterminado" /> : <C2StatusChip tone="neutral" label="Disponible" />),
    },
  ];

  if (languagedata.loading) return <CircularLoading />;

  const editingText = texts ? Object.keys(texts.edits).length : 0;
  const langOptionValue = meta ? LANG_OPTIONS.find((o) => o.code === meta.langLocale) || null : null;
  const dateOptionValue = meta ? DATE_OPTIONS.find((o) => o.code === meta.dateLocale) || null : null;

  return (
    <ThemeProvider theme={c2TableTheme}>
      {!canEdit ? <DemoModeAlert /> : null}
      <SettingsNotice notice={notice} onClose={() => setNotice(null)} />
      <C2TableCard>
        <C2SectionHeading
          title="Idiomas de la app"
          subtitle="Al agregar un idioma, todos los textos se traducen automáticamente desde el predeterminado. Luego puedes corregirlos uno por uno."
        />
          <MaterialTable
            title={t('language_cap')}
            columns={columns}
          data={languages}
          style={c2TableStyle(isRTL)}
          components={c2Toolbar(
            <C2AddButton key="add" label="Agregar idioma" onClick={() => guardEdit() && setMeta({ langName: '', langLocale: '', dateLocale: '' })} />
          )}
          onRowClick={(evt, row) => setSelectedRow(row.tableData.id)}
          options={{ ...c2TableOptions(selectedRow), actionsColumnIndex: -1, exportButton: false }}
          localization={c2Localization(
            localization(t),
            <C2EmptyState icon={TranslateOutlinedIcon} title="No hay idiomas configurados" message="Agrega el primer idioma para que la app muestre sus textos." />
          )}
            actions={[
            (row) => ({
              icon: () => <C2DocsChip icon={EditNoteOutlinedIcon} label="Textos" />,
              tooltip: 'Revisar y corregir traducciones',
              onClick: (e, r) => openTexts(r),
            }),
            (row) => ({
              icon: () => <C2DocsChip icon={StarOutlineRoundedIcon} label="Predeterminar" />,
              tooltip: 'Usar como idioma predeterminado',
              hidden: !!row.default,
              onClick: (e, r) => makeDefault(r),
            }),
            (row) => ({
              icon: () => <TuneOutlinedIcon />,
              tooltip: 'Editar nombre y formato',
              onClick: (e, r) => guardEdit() && setMeta({ id: r.id, langName: r.langName, langLocale: r.langLocale, dateLocale: r.dateLocale }),
            }),
            (row) => ({
              icon: () => <DeleteOutlineRoundedIcon sx={{ color: row.default ? C2.border : '#B91C1C' }} />,
              tooltip: row.default ? 'No se puede eliminar el idioma predeterminado' : 'Eliminar idioma',
              disabled: !!row.default,
              onClick: (e, r) => guardEdit() && setToDelete(r),
            }),
          ]}
        />
      </C2TableCard>

      <C2Dialog
        open={!!meta}
        onClose={() => !busy && setMeta(null)}
        title={meta?.id ? 'Editar idioma' : 'Agregar idioma'}
        icon={TranslateOutlinedIcon}
        maxWidth={480}
        actions={
          <>
            <C2SecondaryButton onClick={() => setMeta(null)} disabled={busy}>Cancelar</C2SecondaryButton>
            <C2PrimaryButton onClick={saveMeta} disabled={busy}>
              {busy ? (progress ? 'Traduciendo…' : 'Guardando…') : meta?.id ? 'Guardar' : 'Agregar y traducir'}
            </C2PrimaryButton>
          </>
        }
      >
        {meta ? (
          <Box>
            <SettingsField
              name="langName"
              label="Nombre visible"
              value={meta.langName}
              onChange={(n, v) => setMeta({ ...meta, langName: v, error: null })}
              placeholder="Português"
              disabled={busy}
            />
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={7}>
                <Autocomplete
                  options={LANG_OPTIONS}
                  value={langOptionValue}
                  onChange={(e, v) => setMeta({ ...meta, langLocale: v ? v.code : '', dateLocale: meta.dateLocale || (v && datelocales[v.code] ? v.code : ''), error: null })}
                  isOptionEqualToValue={(o, v) => o.code === v.code}
                  disabled={busy || !!meta.id}
                  renderInput={(params) => <TextField {...params} label="Idioma" margin="dense" helperText={meta.id ? 'No se puede cambiar' : ' '} sx={settingsFieldSx} />}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <Autocomplete
                  options={DATE_OPTIONS}
                  value={dateOptionValue}
                  onChange={(e, v) => setMeta({ ...meta, dateLocale: v ? v.code : '', error: null })}
                  isOptionEqualToValue={(o, v) => o.code === v.code}
                  disabled={busy}
                  renderInput={(params) => <TextField {...params} label="Formato de fecha" margin="dense" helperText=" " sx={settingsFieldSx} />}
                />
              </Grid>
            </Grid>
            {!meta.id ? (
              <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.84rem', color: C2.muted }}>
                Se traducirán {defaultLang?.keysCount || 0} textos desde {defaultLang?.langName || 'el idioma predeterminado'}. Puede tardar unos minutos; no cierres esta ventana.
              </Typography>
            ) : null}
            {progress ? (
              <Box sx={{ mt: 1.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress.total ? (progress.done / progress.total) * 100 : 0}
                  sx={{ height: 8, borderRadius: 4, backgroundColor: C2.lavender, '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${C2.violet}, ${C2.bright})` } }}
                />
                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.78rem', color: C2.muted, mt: 0.5 }}>
                  {progress.done} de {progress.total} textos traducidos
                </Typography>
              </Box>
            ) : null}
            {meta.error ? <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.84rem', color: '#B91C1C', mt: 1 }}>{meta.error}</Typography> : null}
          </Box>
        ) : null}
      </C2Dialog>

      <C2Dialog
        open={!!texts}
        onClose={() => !busy && setTexts(null)}
        title={texts ? `Textos · ${texts.langName}` : ''}
        icon={EditNoteOutlinedIcon}
        maxWidth={860}
        fullWidth
        actions={
          <>
            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.84rem', color: C2.muted, mr: 'auto' }}>
              {editingText ? `${editingText} texto${editingText === 1 ? '' : 's'} modificado${editingText === 1 ? '' : 's'}` : 'Sin cambios'}
            </Typography>
            <C2SecondaryButton onClick={() => setTexts(null)} disabled={busy}>Cancelar</C2SecondaryButton>
            <C2PrimaryButton onClick={saveTexts} disabled={busy || !editingText}>{busy ? 'Guardando…' : 'Guardar textos'}</C2PrimaryButton>
          </>
        }
      >
        {texts ? (
          <Box>
            <TextField
              fullWidth
              margin="dense"
              placeholder="Buscar por clave o texto…"
              value={texts.search}
              onChange={(e) => setTexts({ ...texts, search: e.target.value })}
              InputProps={{ startAdornment: <SearchRoundedIcon sx={{ color: C2.muted, mr: 1 }} /> }}
              sx={settingsFieldSx}
            />
            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.78rem', color: C2.muted, my: 1 }}>
              {textRows.total > MAX_VISIBLE_KEYS
                ? `Mostrando ${MAX_VISIBLE_KEYS} de ${textRows.total}. Usa el buscador para encontrar un texto.`
                : `${textRows.total} texto${textRows.total === 1 ? '' : 's'}`}
            </Typography>
            <Box sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
              {textRows.rows.map(([key, value]) => (
                <TextField
                  key={key}
                  fullWidth
                  margin="dense"
                  size="small"
                  label={key}
                  value={key in texts.edits ? texts.edits[key] : value}
                  onChange={(e) => setTexts({ ...texts, edits: { ...texts.edits, [key]: e.target.value } })}
                  sx={settingsFieldSx}
                />
              ))}
            </Box>
          </Box>
        ) : null}
      </C2Dialog>

      <C2Dialog
        open={!!toDelete}
        onClose={() => !busy && setToDelete(null)}
        title="Eliminar idioma"
        icon={DeleteOutlineRoundedIcon}
        actions={
          <>
            <C2SecondaryButton onClick={() => setToDelete(null)} disabled={busy}>Cancelar</C2SecondaryButton>
            <C2DangerButton onClick={confirmDelete} disabled={busy}>{busy ? 'Eliminando…' : 'Eliminar'}</C2DangerButton>
          </>
        }
      >
        <Typography sx={{ fontFamily: C2_FONT, color: C2.muted }}>
          ¿Eliminar {toDelete?.langName} y todas sus traducciones? Dejará de estar disponible en la app y en la web.
        </Typography>
      </C2Dialog>
    </ThemeProvider>
  );
}
